import type { SettingsBag, Vec2 } from '../../core/model';
import type { StudioApi } from '../../engine/api';
import { sniffMimeType } from '../../persist/assets';
import { errorMessage } from '../safe';

/** Source kind that plays a dropped file, by MIME type; null for unsupported files. */
export function sourceKindForFile(file: { type: string; name: string }): 'image' | 'media' | null {
  const type = file.type.toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/') || type.startsWith('audio/')) return 'media';
  const extension = file.name.toLowerCase().split('.').pop() ?? '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'svg', 'bmp'].includes(extension)) return 'image';
  if (['mp4', 'webm', 'mov', 'mkv', 'm4v', 'mp3', 'wav', 'ogg', 'm4a', 'flac', 'opus'].includes(extension)) return 'media';
  return null;
}

/** As sourceKindForFile, falling back to the file's leading bytes when type and extension say nothing. */
export async function sourceKindForBytes(file: File): Promise<'image' | 'media' | null> {
  const kind = sourceKindForFile(file);
  if (kind) return kind;
  const sniffed = sniffMimeType(new Uint8Array(await file.slice(0, 512).arrayBuffer()));
  return sniffed ? sourceKindForFile({ type: sniffed, name: file.name }) : null;
}

export function nameFromFile(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, '').trim();
  return base || fileName;
}

/** Key of the first asset property a source type declares; that is where the asset id goes. */
export function assetSettingKey(studio: Pick<StudioApi, 'registry'>, kind: string): string | null {
  const def = studio.registry.getSource(kind);
  if (!def) return null;
  try {
    const descriptor = def.properties(def.defaults()).find((p) => p.type === 'asset');
    return descriptor?.key ?? null;
  } catch {
    return null;
  }
}

/** Imports each file as an asset and adds an image/media source for it at `point`. Returns the new item ids. */
export async function addDroppedFiles(
  studio: StudioApi,
  sceneId: string,
  files: readonly File[],
  point: Vec2,
): Promise<string[]> {
  const created: string[] = [];
  let offset = 0;
  for (const file of files) {
    const kind = await sourceKindForBytes(file).catch(() => null);
    if (!kind) {
      studio.notify('warn', `"${file.name}" is not an image, video or audio file.`);
      continue;
    }
    const key = assetSettingKey(studio, kind);
    if (!key) {
      studio.notify('error', `No "${kind}" source type is available for "${file.name}".`);
      continue;
    }
    try {
      const asset = await studio.importAsset(file, file.name);
      const settings: SettingsBag = { [key]: asset.id };
      const { itemId } = studio.commands.addSourceToScene(sceneId, kind, {
        name: nameFromFile(file.name),
        settings,
        transform: { position: { x: Math.round(point.x + offset), y: Math.round(point.y + offset) } },
      });
      created.push(itemId);
      offset += 24;
    } catch (error) {
      studio.notify('error', `Could not add "${file.name}": ${errorMessage(error)}`);
    }
  }
  return created;
}
